import { useState } from 'react'

import { useTheme } from '@emotion/react'
import { UseFormReturn } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import {
  AllocationDistribution,
  AllocationDistributionDestinationAndShare,
} from '@cash-web/protos/squareup/cash/paychecks/AllocationDistribution.pb'
import { AllocationDestination } from '@cash-web/protos/squareup/cash/paychecks/common.pb'
import { Color } from '@cash-web/protos/squareup/cash/ui/Color.pb'
import { getDestinationId } from '@cash-web/shared-feature-paychecks'
import { createArcs } from '@cash-web/shared-feature-pie-chart'

import { getDisplayNameFromAllocationDestination } from './getDisplayNameFromAllocationDestination'

const getTokenFromAllocationDesination = (allocationDestination?: AllocationDestination) => {
  if (allocationDestination?.investing) {
    return allocationDestination.investing.investment_entity_token
  }
  if (allocationDestination?.savings) {
    const allocationIdentifier = allocationDestination.savings.identifier
    if (allocationIdentifier?.goal_folder) {
      return allocationIdentifier.goal_folder.goal_folder_token
    }
    if (allocationIdentifier?.general_folder) {
      return 'savings-general'
    }
  }

  return undefined
}

const isSameAllocationDestination = (destinationA?: AllocationDestination, destinationB?: AllocationDestination) => {
  return getTokenFromAllocationDesination(destinationA) === getTokenFromAllocationDesination(destinationB)
}

const useArcs = (
  allocations: AllocationDistributionDestinationAndShare[],
  allocationData: {
    label: string
    allocation_destination: AllocationDestination
    color: string
    value: number
  },
  tintColors: Color[]
) => {
  const { darkMode } = useTheme()
  const slices = allocations.map(({ allocation_destination, share_in_basis_points }, index) => {
    const id = (allocation_destination && getDestinationId(allocation_destination)) ?? ''
    const label = getDisplayNameFromAllocationDestination(allocation_destination) ?? id
    const token = getTokenFromAllocationDesination(allocation_destination)
    const currentToken = getTokenFromAllocationDesination(allocationData.allocation_destination)
    const color = tintColors[index][darkMode ? 'dark' : 'light']?.srgb ?? ''
    return {
      id: label,
      label,
      color: `${color}${id === 'cashBalance' || token === currentToken ? '' : '33'}`,
      percentage: (share_in_basis_points ?? 0) / 100,
    }
  })
  return createArcs(slices)
}

type UseBlockerMultiAllocationArgs = {
  form: UseFormReturn<AllocationDistribution>
  allocationData: { label: string; allocation_destination: AllocationDestination; color: string; value: number }
  tintColors: Color[]
  setShouldShowManualInput: (value: boolean) => void
}
export const useBlockerMultiAllocation = ({
  form,
  allocationData,
  tintColors,
  setShouldShowManualInput,
}: UseBlockerMultiAllocationArgs) => {
  const { t } = useTranslation()
  const allocations = form.watch('allocations') ?? []

  const handleBasisChange = (basis: number) => {
    const maxBasis = Math.min(basis, totalAvailableAllocation)

    const updatedAllocations =
      allocations.map((allocation: AllocationDistributionDestinationAndShare) =>
        isSameAllocationDestination(allocation.allocation_destination, allocationData.allocation_destination)
          ? { ...allocation, share_in_basis_points: maxBasis }
          : allocation
      ) ?? []

    const totalOfNewAllocationsWithoutCashBalance = updatedAllocations.reduce(
      (acc: number, { allocation_destination, share_in_basis_points }: AllocationDistributionDestinationAndShare) =>
        allocation_destination?.['cashBalance'] ? acc : acc + (share_in_basis_points ?? 0),
      0
    )

    const finalAllocations = updatedAllocations.map((allocation: AllocationDistributionDestinationAndShare) =>
      allocation.allocation_destination?.['cashBalance']
        ? { ...allocation, share_in_basis_points: 10000 - totalOfNewAllocationsWithoutCashBalance }
        : allocation
    )

    form.setValue('allocations', finalAllocations)
  }

  const allocationBasis = allocations.find(allocation =>
    isSameAllocationDestination(allocation.allocation_destination, allocationData.allocation_destination)
  )?.share_in_basis_points

  const basisParsed = `${allocationBasis ? allocationBasis / 100 : 0}%`

  const totalAvailableAllocation = allocations.reduce(
    (acc, { allocation_destination, share_in_basis_points }) =>
      isSameAllocationDestination(allocation_destination, allocationData.allocation_destination) ||
      allocation_destination?.['cashBalance']
        ? acc
        : acc - (share_in_basis_points ?? 0),
    10000
  )

  const [formattedValue, setFormattedValue] = useState<string>(basisParsed)
  const handleSetFormattedValue = (value: string) => {
    const basis = Number(value.replace('%', '')) * 100
    if (basis < 0 || Number.isNaN(basis)) {
      setFormattedValue('')
      return
    }
    setFormattedValue(value)
    handleBasisChange(basis)
  }

  const confirmCustomAmount = () => {
    handleSetFormattedValue(formattedValue)
    setFormattedValue('')
    setShouldShowManualInput(false)
  }

  const arcs = useArcs(allocations, allocationData, tintColors)

  const avatarIcon = allocationData.allocation_destination.investing?.ui_specification?.icon
  const avatarUrl = avatarIcon?.light_url ?? avatarIcon?.dark_url

  const iconUnicode = allocationData.allocation_destination.savings?.ui_specification?.icon_unicode

  return {
    allocationBasis,
    avatarUrl,
    iconUnicode,
    confirmCustomAmount,
    formattedValue,
    setFormattedValue,
    handleSetFormattedValue,
    basisParsed,
    totalAvailableAllocation,
    handleBasisChange,
    arcs,
    hero: { color: allocationData.color, value: basisParsed, label: t('of each paycheck', { context: 'label' }) },
  }
}
